import { ENTRY_SEARCH } from '@utils/entrySearch';
import {
  captureFirstTouch,
  removeFirstTouchFromLocalStorage,
} from '@utils/captureFirstTouch';

/**
 * A origem de aquisição de quem chega no app: os parâmetros de campanha da URL de entrada.
 *
 * Mesmo caminho do `referrerCode` (`utils/captureRef.ts`): lê da URL na entrada, guarda no
 * `localStorage` e manda no cadastro. O que muda é a regra de escrita — aqui vale a PRIMEIRA
 * origem vista, não a última. Ver `saveAttributionToLocalStorage`.
 *
 * São identificadores de campanha, não dado pessoal: dizem qual anúncio trouxe a pessoa, nunca
 * quem ela é.
 */

/**
 * Os sete parâmetros e o teto de cada um, espelhando o `varchar` da coluna (changeset 636).
 *
 * Truncar aqui além de truncar na API: o limite pertence ao dado, e quem escreve a URL é a
 * plataforma de anúncio — nome de campanha e de criativo passam de 255 com facilidade. Cortar
 * perde precisão do rótulo; deixar chegar inteiro arriscaria erro de coluna, e derrubar um
 * cadastro por causa de um parâmetro de marketing seria trocar receita por métrica.
 */
const CAMPOS = {
  utm_source: { chave: 'utmSource', max: 120 },
  utm_medium: { chave: 'utmMedium', max: 120 },
  utm_campaign: { chave: 'utmCampaign', max: 255 },
  utm_content: { chave: 'utmContent', max: 255 },
  utm_term: { chave: 'utmTerm', max: 255 },
  gclid: { chave: 'gclid', max: 255 },
  fbclid: { chave: 'fbclid', max: 255 },
} as const;

type Chave = (typeof CAMPOS)[keyof typeof CAMPOS]['chave'];

export type Attribution = Partial<Record<Chave, string>>;

export const ATTRIBUTION_LOCAL_STORAGE_KEY = 'attribution';

/**
 * Por quanto tempo uma origem guardada continua valendo.
 *
 * O `localStorage` é durável, e a origem só era apagada depois de um cadastro concluído. Quem chega
 * por um anúncio e faz LOGIN numa conta que já tem não gera cadastro nenhum — e a campanha ficava
 * guardada para sempre naquele navegador, pronta para ser carimbada num cadastro meses depois (de
 * outra pessoa no mesmo aparelho, inclusive) e, pior, para BLOQUEAR a campanha nova, já que a
 * escrita é de primeiro toque.
 *
 * Trinta dias é a janela de clique usual do mercado (o Google Ads usa 30, o Meta 7) e é folgada
 * para o que se mede aqui: 89% de quem cria comunidade cria em até 24h do cadastro. O platform-api
 * tem a mesma proteção no caminho SSO, com `ATTRIBUTION_WINDOW_MINUTES`, e pela mesma razão.
 */
export const ATTRIBUTION_TTL_DAYS = 30;

const TTL_MS = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;

/** O que vai para o `localStorage`: a origem mais QUANDO ela foi vista. */
type AttributionGuardada = {
  attribution: Attribution;
  capturedAt: string;
};

const expirou = (capturedAt: unknown) => {
  if (typeof capturedAt !== 'string') return true;

  const quando = new Date(capturedAt).getTime();

  // Data ilegível conta como expirada: sem instante não há como afirmar que a origem ainda vale.
  return Number.isNaN(quando) || Date.now() - quando > TTL_MS;
};

/** Só caracteres de controle; o resto do texto (acento, espaço, sinal) passa. */
const CARACTERES_DE_CONTROLE = /[\u0000-\u001F\u007F-\u009F]/g;

/**
 * Os parâmetros de campanha presentes numa query string, saneados e truncados.
 *
 * Devolve objeto vazio quando não há nenhum — que é o caso da maioria das visitas (tráfego direto
 * e orgânico) e não é erro.
 */
export const parseAttributionFromSearch = (search: string): Attribution => {
  const params = new URLSearchParams(search);

  return Object.entries(CAMPOS).reduce<Attribution>(
    (acc, [param, { chave, max }]) => {
      const valor = (params.get(param) ?? '')
        .replace(CARACTERES_DE_CONTROLE, '')
        .trim();

      if (valor) acc[chave] = valor.slice(0, max);

      return acc;
    },
    {},
  );
};

/**
 * Guarda a origem, **sem sobrescrever uma já guardada**.
 *
 * É a regra que faz a atribuição ser de primeiro toque. Quem chega por anúncio, navega, sai e
 * volta pelo orgânico antes de se cadastrar continua atribuído ao anúncio — sobrescrever a cada
 * carregamento entregaria o cadastro ao último clique, que é justamente o clique que a gente NÃO
 * pagou. Como toda entrada no app é carregamento novo, sem isso a origem paga se perderia em
 * qualquer segunda visita.
 *
 * Escrita atômica por chamada: ou grava o conjunto inteiro visto nesta URL, ou não grava nada. Um
 * merge com o que já existe misturaria a campanha de uma visita com o criativo de outra e
 * produziria uma linha que nunca existiu.
 */
export const saveAttributionToLocalStorage = (attribution: Attribution) => {
  if (!Object.keys(attribution).length) return;
  // Primeiro toque, mas dentro do prazo: origem vencida não bloqueia a campanha nova.
  if (Object.keys(getAttributionFromLocalStorage()).length) return;

  const guardada: AttributionGuardada = {
    attribution,
    capturedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(
      ATTRIBUTION_LOCAL_STORAGE_KEY,
      JSON.stringify(guardada),
    );
  } catch {
    /*
      Armazenamento indisponível (aba privada, cota estourada, storage bloqueado) não pode virar
      exceção: isto roda na entrada do app, antes de qualquer tela, e a medição de marketing não
      vale interromper a visita.
    */
  }
};

/**
 * A origem guardada, ou objeto vazio.
 *
 * Nunca lança. É lido no meio do envio do cadastro, dentro do `try` que reporta "erro ao criar o
 * cadastro" — um JSON corrompido no `localStorage` (de uma versão anterior, de outra aba, de
 * qualquer coisa) faria a pessoa ver falha num cadastro que nem chegou a ser tentado.
 */
export const getAttributionFromLocalStorage = (): Attribution => {
  try {
    const bruto = localStorage.getItem(ATTRIBUTION_LOCAL_STORAGE_KEY);
    if (!bruto) return {};

    const guardado = JSON.parse(bruto);
    if (!guardado || typeof guardado !== 'object' || Array.isArray(guardado))
      return {};

    /*
      Sem instante, ou fora do prazo, a origem deixa de existir — e é APAGADA, para que a próxima
      campanha possa ser guardada em vez de esbarrar na regra de primeiro toque. Um formato antigo,
      anterior ao carimbo de tempo, cai aqui pelo mesmo caminho.
    */
    if (expirou(guardado.capturedAt)) {
      apagarCampanha();
      return {};
    }

    const campanha = guardado.attribution;
    if (!campanha || typeof campanha !== 'object') return {};

    return Object.values(CAMPOS).reduce<Attribution>((acc, { chave, max }) => {
      const valor = campanha[chave];
      if (typeof valor === 'string' && valor.trim())
        acc[chave] = valor.trim().slice(0, max);

      return acc;
    }, {});
  } catch {
    return {};
  }
};

/** Só a campanha: é o que expira sozinho, sem levar junto o primeiro toque, que tem prazo próprio. */
const apagarCampanha = () => {
  try {
    localStorage.removeItem(ATTRIBUTION_LOCAL_STORAGE_KEY);
  } catch {
    /* Mesmo motivo do save: limpeza de metadado não interrompe o pós-cadastro. */
  }
};

/**
 * Apaga TODA a origem guardada — a campanha e o primeiro toque (`utils/captureFirstTouch`).
 *
 * Os dois explicam o mesmo cadastro e morrem pelos mesmos motivos: o cadastro aconteceu, ou há sessão
 * neste navegador e o clique anterior já não pode explicar um cadastro novo. Quem chama não precisa
 * saber que são duas memórias.
 */
export const removeAttributionFromLocalStorage = () => {
  apagarCampanha();
  removeFirstTouchFromLocalStorage();
};

/**
 * Lê a query da ENTRADA e guarda a origem, se for a primeira. Chamado na entrada do app.
 *
 * O padrão é o instantâneo de `ENTRY_SEARCH`, e não `window.location.search`: quem chama é um
 * efeito do `_app`, que corre depois da pintura — um clique num link já hidratado nessa janela
 * troca a URL antes, e a origem da entrada se perderia. Ver `utils/entrySearch.ts`.
 */
export const captureAttributionFromUrl = (search: string = ENTRY_SEARCH) => {
  saveAttributionToLocalStorage(parseAttributionFromSearch(search));
  // Mesma entrada, mesma regra de sessão (`useCapturaDeOrigem`): o primeiro toque vai junto.
  captureFirstTouch({ search });
};
